import {useDispatch, useSelector} from "react-redux";
import {useEffect, useState} from "react";
import {Alert, Button, Grid, Input, ProgressBar, styles, Surface} from "../../tmwx-design-web/src";
import {useTranslation} from "react-i18next";
import {checkFieldError, isEmpty} from "../../gui-common/appHelper";
import {RootState} from "../../gui-common/redux/store";
import {ResponseBase} from "../../gui-common/redux/data";
import {setSnackbarData} from "../../gui-common/redux/action/snackbarAction";
import {Company} from "../../gui-common/redux/data/company";
import {getFieldErrorText} from "../../utils/helper";
import {usePermissions} from "tmwx-design-web/src/tmwx-design-common";
import {
    AddLocationRequest,
    initialAddLocationRequest,
    isAddLocationRequestValid,
    Location
} from "gui-common/redux/data/location";
import {clearLocationError, clearLocationResponse} from "gui-common/redux/action/atr/locationAction";
import {addNewLocation, getATRLocationList} from "gui-common/redux/action/efe/efeLocationAction";

interface HandleAddLocationProps {
    actionType: 'edit' | 'add'
    item?: Location | null
    onClose?: () => void
}

const HandleAddLocation: React.FC<HandleAddLocationProps> = ({actionType, item, onClose = () => {}}) => {
    const {t} = useTranslation()
    const dispatch: any = useDispatch();
    const {hasRights} = usePermissions()
    const serverError = useSelector<RootState, ResponseBase | null>(state => state.atrLocation.error);
    const isLoading = useSelector<RootState, boolean>(state => state.atrLocation.isLoading);
    const response = useSelector<RootState, ResponseBase | null>(state => state.atrLocation.response);
    const activeCompany = useSelector<RootState, Company>(state => state.auth.activeCompany);
    const [validationMessage, setValidationMessage] = useState<string>("");
    const [request, setRequest] = useState<AddLocationRequest>(initialAddLocationRequest);
    const hasCompanyCreateOrEditRight = hasRights(['efe_efe_list'])

    const handleClear = () => {
        setRequest(initialAddLocationRequest);
        dispatch(clearLocationResponse())
        dispatch(clearLocationError())
        onClose()
    }

    const handleLocationClick = () => {
        const nextRequest = {
            ...request,
            companyId: activeCompany.id
        }

        if (item && actionType === 'edit') {
            ///modify
            setValidationMessage('')
        } else {
            //save
            setValidationMessage('')
            if (isAddLocationRequestValid(nextRequest as AddLocationRequest)) {
                dispatch(addNewLocation(nextRequest as AddLocationRequest));
            } else {
                setValidationMessage(t('mandatoryFields'));
            }
        }
    }

    /* HANDLE EDIT REQUEST VALUES */
    useEffect(() => {
        if (item) {

            let obj: AddLocationRequest = {
                address: item.address ? item.address : '',
                companyId: -1,
                longitude: 47.530519197005376,
                latitude: 19.034884013536047
            }

            setRequest(obj)
        }
    }, [item])

    /* HANDLE RESPONSE */
    useEffect(() => {
        if (response) {
            if (response.responseCode === 'SUCCESS') {
                handleClear()
                dispatch(setSnackbarData({
                    status: 'success',
                    text: t('actionSuccess')
                }))

                if (activeCompany.id !== undefined) {
                    dispatch(getATRLocationList(1, activeCompany.id))
                }
            } else {
                dispatch(setSnackbarData({
                    status: 'danger',
                    text: t('actionFailed')
                }))
            }
        }
    }, [response])


    return (
        <Surface elevation={0}>
            {isLoading && <ProgressBar animated rounded={false} width={'100%'} height={6} vertical={false}/>}

            <Grid.Container centered maxWidth="xl">
                <Grid.Col m={12} lg={12} xl={12}>
                    {serverError && (
                        <Alert style={styles.marginTop8} status="danger" text={t('errorOccurred')}/>
                    )}

                    {!isEmpty(validationMessage) && (
                        <Alert style={styles.marginTop8} text={validationMessage} status="danger"/>
                    )}

                    <Input
                        style={styles.marginTop8}
                        fullWidth
                        error={checkFieldError('locationAddress', serverError)}
                        icon={"location_on_outlined"}
                        placeholder={t('atr.machines.location')}
                        value={request.address}
                        onChangeValue={(text) => setRequest({...request, address: text})}
                        helperText={getFieldErrorText('locationAddress', serverError, t)}
                    />

                    <Input
                        style={styles.marginTop8}
                        fullWidth
                        error={checkFieldError('latitude', serverError)}
                        icon={"swap_horiz"}
                        placeholder={t('atr.locations.latitude')}
                        value={request.latitude}
                        onChangeValue={(text) => setRequest({...request, latitude: text})}
                        helperText={getFieldErrorText('latitude', serverError, t)}
                    />

                    <Input
                        style={styles.marginTop8}
                        fullWidth
                        error={checkFieldError('longitude', serverError)}
                        icon={"swap_vert"}
                        placeholder={t('atr.locations.longitude')}
                        value={request.longitude}
                        onChangeValue={(text) => setRequest({...request, longitude: text})}
                        helperText={getFieldErrorText('longitude', serverError, t)}
                    />

                    {hasCompanyCreateOrEditRight && (
                        <div style={{...styles.marginTop8, ...styles.row, ...{justifyContent: 'flex-end'}}}>
                            <Button status="danger" variant="outlined" onPress={handleClear}>
                                {t('cancel')}
                            </Button>
                            <Button style={styles.marginLeft16} onPress={handleLocationClick}>
                                {actionType === 'add' ? t('add') : t('modify')}
                            </Button>
                        </div>
                    )}
                </Grid.Col>
            </Grid.Container>
        </Surface>
    )
};

export default HandleAddLocation;
